import React from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add,
  Delete,
  DirectionsCar,
  ExpandLess,
  ExpandMore,
  Search,
} from "@mui/icons-material";
import InputAdornment from "@mui/material/InputAdornment";
import {
  VehicleCatalogBrandWithId,
  VehicleCatalogModelEntry,
  VehicleTypeEntry,
  adminGetBrands,
  adminCreateBrand,
  adminAddModel,
  adminDeleteBrand,
  adminDeleteModel,
  adminListVehicleTypes,
  adminCreateVehicleType,
  adminDeleteVehicleType,
  setModelVehicleType,
} from "../../../services/vehicleCatalog.service";
import { useAlert } from "../../../context/AlertContext";

export default function VehicleCatalog() {
  const { t } = useTranslation();
  const { setAlert } = useAlert();

  // --- Tab state ---
  const [tab, setTab] = React.useState(0);

  // --- Search state ---
  const [searchQuery, setSearchQuery] = React.useState("");

  // --- Brands tab state ---
  const [brands, setBrands] = React.useState<VehicleCatalogBrandWithId[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  // Expand/collapse state — null means none expanded
  const [openBrandId, setOpenBrandId] = React.useState<string | null>(null);

  // Add Brand dialog
  const [addBrandOpen, setAddBrandOpen] = React.useState(false);
  const [newBrandName, setNewBrandName] = React.useState("");
  const [addBrandLoading, setAddBrandLoading] = React.useState(false);

  // Delete Brand confirm dialog
  const [deleteBrandId, setDeleteBrandId] = React.useState<string | null>(null);
  const [deleteBrandLoading, setDeleteBrandLoading] = React.useState(false);

  // Add Model — per-brand input state
  const [newModelName, setNewModelName] = React.useState<
    Record<string, string>
  >({});
  const [addModelLoading, setAddModelLoading] = React.useState<
    Record<string, boolean>
  >({});

  // Per-model vehicle-type update spinner
  const [vtUpdateLoading, setVtUpdateLoading] = React.useState<
    Record<string, boolean>
  >({});

  // --- Vehicle Types tab state ---
  const [vehicleTypes, setVehicleTypes] = React.useState<VehicleTypeEntry[]>([]);
  const [vtLoading, setVtLoading] = React.useState(true);
  const [vtError, setVtError] = React.useState<string | null>(null);
  const [newVtKey, setNewVtKey] = React.useState("");
  const [newVtLabel, setNewVtLabel] = React.useState("");
  const [newVtOrder, setNewVtOrder] = React.useState<string>("0");
  const [addVtLoading, setAddVtLoading] = React.useState(false);
  const [deleteVtKey, setDeleteVtKey] = React.useState<string | null>(null);
  const [deleteVtLoading, setDeleteVtLoading] = React.useState(false);

  const fetchBrands = React.useCallback(async () => {
    try {
      setError(null);
      const data = await adminGetBrands();
      setBrands(data);
    } catch (err: any) {
      setError(err.message || t("vehicleCatalog.loadBrandsFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchVehicleTypes = React.useCallback(async () => {
    try {
      setVtError(null);
      const data = await adminListVehicleTypes();
      setVehicleTypes(data);
    } catch (err: any) {
      setVtError(err.message || t("vehicleCatalog.loadVehicleTypesFailed"));
    } finally {
      setVtLoading(false);
    }
  }, [t]);

  React.useEffect(() => {
    fetchBrands();
    fetchVehicleTypes();
  }, [fetchBrands, fetchVehicleTypes]);

  // --- Add Brand ---
  const handleAddBrand = async () => {
    if (!newBrandName.trim()) return;
    setAddBrandLoading(true);
    try {
      await adminCreateBrand(newBrandName.trim());
      setAlert("success", t("vehicleCatalog.brandCreated"));
      setAddBrandOpen(false);
      setNewBrandName("");
      fetchBrands();
    } catch (err: any) {
      setAlert(
        "error",
        err.response?.data?.message ||
          err.message ||
          t("vehicleCatalog.brandDeleteFailed")
      );
    } finally {
      setAddBrandLoading(false);
    }
  };

  // --- Delete Brand ---
  const handleDeleteBrandConfirm = async () => {
    if (!deleteBrandId) return;
    setDeleteBrandLoading(true);
    try {
      await adminDeleteBrand(deleteBrandId);
      setAlert("success", t("vehicleCatalog.brandDeleted"));
      setDeleteBrandId(null);
      if (openBrandId === deleteBrandId) setOpenBrandId(null);
      fetchBrands();
    } catch (err: any) {
      setAlert("error", err.message || t("vehicleCatalog.brandDeleteFailed"));
    } finally {
      setDeleteBrandLoading(false);
    }
  };

  // --- Add Model ---
  const handleAddModel = async (brandId: string) => {
    const model = newModelName[brandId]?.trim();
    if (!model) return;
    setAddModelLoading((prev) => ({ ...prev, [brandId]: true }));
    try {
      await adminAddModel(brandId, model);
      setAlert("success", t("vehicleCatalog.modelAdded", { model }));
      setNewModelName((prev) => ({ ...prev, [brandId]: "" }));
      fetchBrands();
    } catch (err: any) {
      setAlert("error", err.message || t("vehicleCatalog.modelAddFailed"));
    } finally {
      setAddModelLoading((prev) => ({ ...prev, [brandId]: false }));
    }
  };

  // --- Delete Model ---
  // Backend $pulls by name, so passing the model name string still works after
  // the schema change to { name, vehicleType } subdocuments.
  const handleDeleteModel = async (brandId: string, modelName: string) => {
    try {
      await adminDeleteModel(brandId, modelName);
      setAlert("success", t("vehicleCatalog.modelDeleted", { model: modelName }));
      fetchBrands();
    } catch (err: any) {
      setAlert("error", err.message || t("vehicleCatalog.modelDeleteFailed"));
    }
  };

  // --- Set model vehicleType ---
  const handleSetModelVehicleType = async (
    brandId: string,
    modelName: string,
    vehicleType: string
  ) => {
    const loadKey = `${brandId}::${modelName}`;
    setVtUpdateLoading((prev) => ({ ...prev, [loadKey]: true }));
    try {
      const updated = await setModelVehicleType(brandId, modelName, vehicleType);
      // Replace the brand entry in-place to reflect new model array
      setBrands((prev) =>
        prev.map((b) =>
          b._id === updated._id ? { ...b, models: updated.models } : b
        )
      );
      setAlert(
        "success",
        vehicleType
          ? t("vehicleCatalog.modelTypeSet", { model: modelName })
          : t("vehicleCatalog.modelTypeCleared", { model: modelName })
      );
    } catch (err: any) {
      setAlert(
        "error",
        err.response?.data?.message ||
          err.message ||
          t("vehicleCatalog.modelTypeUpdateFailed")
      );
    } finally {
      setVtUpdateLoading((prev) => ({ ...prev, [loadKey]: false }));
    }
  };

  // --- Vehicle Type CRUD ---
  const handleAddVehicleType = async () => {
    const key = newVtKey.trim().toLowerCase();
    const label = newVtLabel.trim();
    const order = Number.parseInt(newVtOrder, 10);
    if (!key || !label || Number.isNaN(order)) {
      setAlert("error", t("vehicleCatalog.fieldsRequired"));
      return;
    }
    setAddVtLoading(true);
    try {
      await adminCreateVehicleType({ key, label, order });
      setAlert("success", t("vehicleCatalog.vehicleTypeCreated", { label }));
      setNewVtKey("");
      setNewVtLabel("");
      setNewVtOrder("0");
      fetchVehicleTypes();
    } catch (err: any) {
      setAlert(
        "error",
        err.response?.data?.message ||
          err.message ||
          t("vehicleCatalog.modelAddFailed")
      );
    } finally {
      setAddVtLoading(false);
    }
  };

  const handleDeleteVehicleTypeConfirm = async () => {
    if (!deleteVtKey) return;
    setDeleteVtLoading(true);
    try {
      await adminDeleteVehicleType(deleteVtKey);
      setAlert("success", t("vehicleCatalog.vehicleTypeDeleted", { label: deleteVtKey }));
      setDeleteVtKey(null);
      fetchVehicleTypes();
    } catch (err: any) {
      // In-use guard (T-21-02-02): surface the backend 400 message verbatim so
      // the admin sees which brand still references the type.
      setAlert(
        "error",
        err.response?.data?.message ||
          err.message ||
          t("vehicleCatalog.vehicleTypeDeleteFailed")
      );
    } finally {
      setDeleteVtLoading(false);
    }
  };

  // Filtered brands — matches brand name or any model name
  const filteredBrands = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return brands;
    return brands
      .map((brand) => {
        const brandMatches = brand.name.toLowerCase().includes(q);
        const matchingModels = brand.models.filter((m) =>
          m.name.toLowerCase().includes(q)
        );
        if (!brandMatches && matchingModels.length === 0) return null;
        return brandMatches ? brand : { ...brand, models: matchingModels };
      })
      .filter((b): b is VehicleCatalogBrandWithId => b !== null);
  }, [brands, searchQuery]);

  // Auto-expand brands that matched via a model when searching
  React.useEffect(() => {
    if (!searchQuery.trim()) return;
    const q = searchQuery.trim().toLowerCase();
    filteredBrands.forEach((brand) => {
      const hasModelMatch = brand.models.some((m) =>
        m.name.toLowerCase().includes(q)
      );
      if (hasModelMatch) setOpenBrandId(brand._id);
    });
  }, [filteredBrands, searchQuery]);

  // Quick lookup helper for displaying labels in chips
  const vtLabelByKey = React.useMemo(() => {
    const map: Record<string, string> = {};
    for (const vt of vehicleTypes) map[vt.key] = vt.label;
    return map;
  }, [vehicleTypes]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box className="w-full">
      {/* Header */}
      <Box className="flex justify-between items-center my-5 shadow-md w-full p-2 rounded">
        <Box className="flex items-center gap-3">
          <DirectionsCar />
          <Typography variant="h6" fontWeight="bold">
            {t("vehicleCatalog.title")}
          </Typography>
          {tab === 0 && (
            <Chip
              label={t("vehicleCatalog.brandsCount", { count: brands.length })}
              color="primary"
              size="small"
              variant="outlined"
            />
          )}
          {tab === 1 && (
            <Chip
              label={t("vehicleCatalog.typesCount", {
                count: vehicleTypes.length,
              })}
              color="primary"
              size="small"
              variant="outlined"
            />
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {tab === 0 && (
            <TextField
              size="small"
              placeholder={t("vehicleCatalog.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: 240 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          )}
          {tab === 0 && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setAddBrandOpen(true)}
            >
              {t("vehicleCatalog.addNewBrand")}
            </Button>
          )}
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label={t("vehicleCatalog.tabBrandsModels")} />
          <Tab label={t("vehicleCatalog.tabVehicleTypes")} />
        </Tabs>
      </Box>

      {/* ── Brands tab ── */}
      {tab === 0 && (
        <Paper sx={{ width: "100%", mb: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("vehicleCatalog.colBrand")}</TableCell>
                  <TableCell>{t("vehicleCatalog.colModels")}</TableCell>
                  <TableCell align="right">{t("common.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredBrands.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        {searchQuery.trim()
                          ? t("vehicleCatalog.noSearchResults", {
                              query: searchQuery,
                            })
                          : t("vehicleCatalog.noBrands")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredBrands.map((brand) => (
                    <React.Fragment key={brand._id}>
                      {/* Brand row */}
                      <TableRow
                        hover
                        onClick={() =>
                          setOpenBrandId((prev) =>
                            prev === brand._id ? null : brand._id
                          )
                        }
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {openBrandId === brand._id ? (
                              <ExpandLess fontSize="small" />
                            ) : (
                              <ExpandMore fontSize="small" />
                            )}
                            <Typography fontWeight="medium">
                              {brand.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={t("vehicleCatalog.modelsCount", {
                              count: brand.models.length,
                            })}
                            size="small"
                          />
                        </TableCell>
                        <TableCell
                          align="right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteBrandId(brand._id)}
                            aria-label={t("vehicleCatalog.deleteBrandAria", { name: brand.name })}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>

                      {/* Expanded models row */}
                      <TableRow>
                        <TableCell colSpan={3} sx={{ py: 0 }}>
                          <Collapse
                            in={openBrandId === brand._id}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box sx={{ p: 2, backgroundColor: "grey.50" }}>
                              {/* Existing models — table with vehicleType dropdown */}
                              {brand.models.length === 0 ? (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 2 }}
                                >
                                  {t("vehicleCatalog.noModels")}
                                </Typography>
                              ) : (
                                <TableContainer
                                  component={Paper}
                                  variant="outlined"
                                  sx={{ mb: 2 }}
                                >
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>{t("vehicleCatalog.colModel")}</TableCell>
                                        <TableCell sx={{ width: 200 }}>
                                          {t("vehicleTypeReviews.vehicleType")}
                                        </TableCell>
                                        <TableCell
                                          align="right"
                                          sx={{ width: 80 }}
                                        >
                                          {t("common.actions")}
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {brand.models.map(
                                        (model: VehicleCatalogModelEntry) => {
                                          const loadKey = `${brand._id}::${model.name}`;
                                          const updating =
                                            !!vtUpdateLoading[loadKey];
                                          return (
                                            <TableRow key={model.name}>
                                              <TableCell>
                                                <Typography variant="body2">
                                                  {model.name}
                                                </Typography>
                                              </TableCell>
                                              <TableCell>
                                                <FormControl
                                                  size="small"
                                                  fullWidth
                                                  disabled={updating}
                                                >
                                                  <InputLabel
                                                    id={`vt-${brand._id}-${model.name}-label`}
                                                  >
                                                    {t(
                                                      "vehicleCatalog.typeSelectLabel"
                                                    )}
                                                  </InputLabel>
                                                  <Select
                                                    labelId={`vt-${brand._id}-${model.name}-label`}
                                                    label={t(
                                                      "vehicleCatalog.typeSelectLabel"
                                                    )}
                                                    value={
                                                      model.vehicleType || ""
                                                    }
                                                    displayEmpty
                                                    onChange={(
                                                      e: SelectChangeEvent<string>
                                                    ) =>
                                                      handleSetModelVehicleType(
                                                        brand._id,
                                                        model.name,
                                                        e.target.value
                                                      )
                                                    }
                                                  >
                                                    <MenuItem value="">
                                                      <em>
                                                        {t(
                                                          "vehicleCatalog.typeNone"
                                                        )}
                                                      </em>
                                                    </MenuItem>
                                                    {vehicleTypes.map((vt) => (
                                                      <MenuItem
                                                        key={vt.key}
                                                        value={vt.key}
                                                      >
                                                        {vt.label}
                                                      </MenuItem>
                                                    ))}
                                                    {/* Render unknown stored value so it remains visible */}
                                                    {model.vehicleType &&
                                                      !vtLabelByKey[
                                                        model.vehicleType
                                                      ] && (
                                                        <MenuItem
                                                          value={
                                                            model.vehicleType
                                                          }
                                                        >
                                                          {t(
                                                            "vehicleCatalog.typeUnknown",
                                                            {
                                                              key: model.vehicleType,
                                                            }
                                                          )}
                                                        </MenuItem>
                                                      )}
                                                  </Select>
                                                </FormControl>
                                              </TableCell>
                                              <TableCell align="right">
                                                <IconButton
                                                  size="small"
                                                  color="error"
                                                  aria-label={t("vehicleCatalog.deleteModelAria", {
                                                    name: model.name,
                                                  })}
                                                  onClick={() =>
                                                    handleDeleteModel(
                                                      brand._id,
                                                      model.name
                                                    )
                                                  }
                                                >
                                                  <Delete fontSize="small" />
                                                </IconButton>
                                              </TableCell>
                                            </TableRow>
                                          );
                                        }
                                      )}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              )}

                              {/* Add model input */}
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  alignItems: "center",
                                }}
                              >
                                <TextField
                                  size="small"
                                  placeholder={t("vehicleCatalog.newModelName")}
                                  value={newModelName[brand._id] ?? ""}
                                  onChange={(e) =>
                                    setNewModelName((prev) => ({
                                      ...prev,
                                      [brand._id]: e.target.value,
                                    }))
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter")
                                      handleAddModel(brand._id);
                                  }}
                                  sx={{ width: 220 }}
                                />
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<Add />}
                                  onClick={() => handleAddModel(brand._id)}
                                  disabled={
                                    addModelLoading[brand._id] ||
                                    !newModelName[brand._id]?.trim()
                                  }
                                >
                                  {t("vehicleCatalog.addModel")}
                                </Button>
                              </Box>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ── Vehicle Types tab ── */}
      {tab === 1 && (
        <Paper sx={{ width: "100%", mb: 2, p: 2 }}>
          {vtLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : vtError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {vtError}
            </Alert>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("vehicleCatalog.colKey")}</TableCell>
                      <TableCell>{t("vehicleCatalog.colLabel")}</TableCell>
                      <TableCell align="center">{t("vehicleCatalog.colOrder")}</TableCell>
                      <TableCell align="right">{t("common.actions")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vehicleTypes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography color="text.secondary" sx={{ py: 3 }}>
                            {t("vehicleCatalog.noVehicleTypes")}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      vehicleTypes.map((vt) => (
                        <TableRow key={vt.key} hover>
                          <TableCell>
                            <Chip
                              label={vt.key}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography fontWeight="medium">
                              {vt.label}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">{vt.order}</TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteVtKey(vt.key)}
                              aria-label={t("vehicleCatalog.deleteVehicleTypeAria", {
                                key: vt.key,
                              })}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Add vehicle type form */}
              <Box
                sx={{
                  mt: 3,
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  size="small"
                  label={t("vehicleCatalog.colKey")}
                  placeholder="suv"
                  value={newVtKey}
                  onChange={(e) => setNewVtKey(e.target.value)}
                  sx={{ width: 140 }}
                  helperText={t("vehicleCatalog.keyHelper")}
                />
                <TextField
                  size="small"
                  label={t("vehicleCatalog.colLabel")}
                  placeholder="SUV"
                  value={newVtLabel}
                  onChange={(e) => setNewVtLabel(e.target.value)}
                  sx={{ width: 180 }}
                />
                <TextField
                  size="small"
                  label={t("vehicleCatalog.colOrder")}
                  type="number"
                  value={newVtOrder}
                  onChange={(e) => setNewVtOrder(e.target.value)}
                  sx={{ width: 100 }}
                />
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleAddVehicleType}
                  disabled={
                    addVtLoading ||
                    !newVtKey.trim() ||
                    !newVtLabel.trim()
                  }
                >
                  {addVtLoading
                    ? t("common.adding")
                    : t("vehicleCatalog.addType")}
                </Button>
              </Box>
            </>
          )}
        </Paper>
      )}

      {/* Add Brand Dialog */}
      <Dialog
        open={addBrandOpen}
        onClose={() => {
          setAddBrandOpen(false);
          setNewBrandName("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("vehicleCatalog.addNewBrand")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t("vehicleCatalog.brandName")}
            fullWidth
            value={newBrandName}
            onChange={(e) => setNewBrandName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAddBrand();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddBrandOpen(false);
              setNewBrandName("");
            }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleAddBrand}
            variant="contained"
            disabled={addBrandLoading || !newBrandName.trim()}
          >
            {addBrandLoading ? t("common.creating") : t("common.create")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Brand Confirm Dialog */}
      <Dialog
        open={!!deleteBrandId}
        onClose={() => setDeleteBrandId(null)}
      >
        <DialogTitle>{t("vehicleCatalog.deleteBrand")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("vehicleCatalog.deleteBrandConfirm")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteBrandId(null)}>{t("common.cancel")}</Button>
          <Button
            onClick={handleDeleteBrandConfirm}
            color="error"
            disabled={deleteBrandLoading}
          >
            {deleteBrandLoading ? t("common.deleting") : t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Vehicle Type Confirm Dialog */}
      <Dialog open={!!deleteVtKey} onClose={() => setDeleteVtKey(null)}>
        <DialogTitle>{t("vehicleCatalog.deleteVehicleType")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("vehicleCatalog.deleteVehicleTypeConfirm", {
              key: deleteVtKey ?? "",
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteVtKey(null)}>{t("common.cancel")}</Button>
          <Button
            onClick={handleDeleteVehicleTypeConfirm}
            color="error"
            disabled={deleteVtLoading}
          >
            {deleteVtLoading ? t("common.deleting") : t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
